<script lang="ts">
  import axios from 'axios';
  import { onMount } from 'svelte';
  import { récupèreStatistiquesMSC, type Statistiques } from '../passerelles/statistiquesMSC';
  import Alerte from '../ui/Alerte.svelte';
  import type { Badge } from '../ui/badge.type';
  import Bouton from '../ui/Bouton.svelte';
  import ChampTexte from '../ui/ChampTexte.svelte';
  import EnteteAutonome from '../ui/EnteteAutonome.svelte';
  import Formulaire from '../ui/Formulaire.svelte';
  import SelectionOrganisation from '../ui/formulaire/SelectionOrganisation.svelte';
  import type { Organisation } from '../ui/formulaire/SelectionOrganisation.types';
  import GroupeDeBadges from '../ui/GroupeDeBadges.svelte';
  import Lien from '../ui/Lien.svelte';
  import { collecteLesErreurs } from '../utils/erreurApi';
  import ConfirmationCreationDemandeAide from './ConfirmationCreationDemandeAide.svelte';
  import type { CorpsAPIDemandeAide } from './DonneesFormulaireDemandeAide';

  interface Props {
    mode?: 'autonome';
    origine: string;
    urlBase?: string;
    cacheLesLiensDeRetour?: boolean;
    siretAidant?: string;
  }

  let {
    mode = undefined,
    origine,
    urlBase = '',
    cacheLesLiensDeRetour = false,
    siretAidant = undefined,
  }: Props = $props();

  let formulaire: Formulaire | undefined = $state();
  let entite: Organisation | undefined = $state();
  let email = $state('');
  let cguSontValidees = $state(false);
  let enSucces = $state(false);
  let enCoursEnvoi = $state(false);
  let erreurs = $state('');
  let erreurValidation = $state(false);
  let badges: Badge[] = $state([]);

  onMount(async () => {
    const statistiques: Statistiques = await récupèreStatistiquesMSC({ urlBase });
    badges = [
      {
        label: `+${statistiques.démarchesDeSécurisationArrondies} organisations accompagnées`,
        accent: 'yellow-tournesol',
      },
      {
        label: `${statistiques.satisfactionUtilisateur}% de satisfaction`,
        accent: 'yellow-tournesol',
      },
    ];
  });

  const soumetsFormulaire = async () => {
    if (!formulaire?.estValide() || !entite) {
      erreurValidation = true;
      return;
    }
    try {
      enCoursEnvoi = true;

      const corps: CorpsAPIDemandeAide = {
        origine,
        entiteAidee: {
          email,
          departement: entite.departement,
          raisonSociale: entite.nom,
          siret: entite.siret,
        },
        siretAidant,
        validationCGU: cguSontValidees,
      };
      const reponse = await axios.post(`${urlBase}/api/mon-aide-cyber/demandes-aide`, corps);
      if (reponse.status === 201) {
        enSucces = true;
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        erreurs = collecteLesErreurs(e.response?.data).join('\n');
      }
    } finally {
      enCoursEnvoi = false;
    }
  };
</script>

{#if !enSucces}
  <Formulaire id="demande-diagnostic-simplifiee" bind:this={formulaire}>
    <div class="formulaire">
      <GroupeDeBadges {badges} />
      {#if mode !== 'autonome'}
        <h3 class="fr-h5">Demande de diagnostic cyber</h3>
      {/if}
      <div class="champ recherche-organisation">
        <label class="libelle" for="entite">Recherchez votre organisation</label>
        <SelectionOrganisation bind:valeur={entite} filtreDepartement={undefined} {urlBase} />
        {#if entite}
          <div>Votre entreprise : {entite.nom} ({entite.departement})</div>
        {/if}
      </div>

      <div class="champ email">
        <ChampTexte
          aideSaisie="Ex : jean.dupont@mail.com"
          id="email"
          libelle="Email de contact"
          messageErreur="Le format du mail est invalide"
          nom="email"
          requis={true}
          type="email"
          bind:valeur={email}
        />
      </div>

      <dsfr-checkbox
        id="cgu"
        name="cgu"
        status={cguSontValidees || !erreurValidation ? 'default' : 'error'}
        value={cguSontValidees}
        onvaluechanged={(e: CustomEvent) => (cguSontValidees = e.detail)}
        errorMessage="Ce champ est obligatoire. Veuillez le cocher."
        required
      >
        <span
          >J’accepte les <Lien
            href="https://monaide.cyber.gouv.fr/cgu"
            libelle="conditions générales d’utilisation"
            blank
          />.
        </span>
      </dsfr-checkbox>

      <div class="envoi-demande">
        <Bouton
          surClic={soumetsFormulaire}
          taille="md"
          libelle="Envoyer ma demande"
          type="primaire"
          etire
          boutonSoumission
          desactive={enCoursEnvoi || undefined}
        />
      </div>
      {#if erreurs}
        <div class="alerte-erreur">
          <Alerte type="erreur" taille="md" titre="Une erreur est survenue">
            {erreurs}
          </Alerte>
        </div>
      {/if}
      {#if mode === 'autonome'}
        <EnteteAutonome {urlBase} />
      {/if}
    </div>
  </Formulaire>
{:else}
  <div class="confirmation">
    <ConfirmationCreationDemandeAide mode="embarque" cacheLesLiens={cacheLesLiensDeRetour} />
  </div>
{/if}

<style lang="scss">
  @use '../../../assets/styles/responsive' as *;

  .formulaire {
    display: flex;
    flex-direction: column;

    .champ {
      display: flex;
      flex-direction: column;
      gap: 8px;

      &.recherche-organisation {
        margin-top: 1rem;
        margin-bottom: 1rem;
      }

      &.email {
        margin-bottom: 2rem;
      }
    }

    dsfr-checkbox {
      margin-bottom: 1.5rem;
    }

    .envoi-demande {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .alerte-erreur {
      margin-top: 32px;
    }

    h3 + .champ.recherche-organisation {
      margin-top: 0;
    }
  }

  .confirmation {
    padding-bottom: 1.5rem;
  }
</style>
